// @flow

import EditIcon from "@mui/icons-material/Edit";
import { FormControl, FormHelperText, FormLabel, TextField } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import {
	DataGridPremium,
	GRID_AGGREGATION_ROOT_FOOTER_ROW_ID,
	GridActionsCellItem,
	GridToolbar
} from "@mui/x-data-grid-premium";
import dayjs from "dayjs";
import { ErrorMessage } from "formik";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import ExportExcelFromUrl from "../../../components/mui/app/exportExcelFromUrl/exportExcelFromUrl";
import CustomAutocomplete from "../../../components/mui/form/autocomplete/autocomplete";
import { CustomPagination } from "../../../components/mui/grids/dataGridCustom/customPagination";
import DataGridWrapper from "../../../components/mui/grids/dataGridWrapper/dataGridWrapper";
import { API } from "../../../helpers/constants";
import { muiGridLocales } from "../../../helpers/mui/muiLocales";
import { access_skills, access_user } from "../../../helpers/rights";
import { getRouteUrl } from "../../../helpers/routing/getRouteUrl";
import { formControlDefaults } from "../../../helpers/theme/defaultProps";
import { useMuiGridServerSide } from "../../../hooks/muiGrid/useMuiGridServerSide";
import useUserData from "../../../hooks/user/useUserData";
import { withPermissionHOC } from "../../permission/components/permissionHOC/permissionHOC";
import { ROUTE_PAGE_PROJECT_DETAILS, ROUTE_PAGE_PROJECT_EDIT } from "../../projects/routes/routes";
import { WorkerGridContainer } from "../components/WorkerGridContainer";
import { REPORTS } from "../locales/namespaces";
import { ProjectGridContainer } from "../components/ProjectGridContainer"
import {DatePicker} from "@mui/x-date-pickers-pro";
import Button from "@mui/material/Button";
type Props = {

};
const initialStartDate = dayjs().startOf('month')
const initialEndDate = dayjs()
const ProjectsReport = (props: Props) => {
	const {t} = useTranslation(REPORTS)
	const options = [{id: 0, Description: t('Active')},
		{id: 1, Description: t('Inactive')},
		{id: 2, Description: t('All')},
	];
	const defaultValue = options.find(option => option.id === 0);
	const [selectedValue, setSelectedValue] = useState(defaultValue);
	const [selectedStartDate, setSelectedStartDate] = useState(initialStartDate)
	const [selectedEndDate, setSelectedEndDate] = useState(initialEndDate)
	const {
		isLoading,
		data,
		total,
		page,
		pageSize,
		filterModel,
		sortModel,
		onPaginationModelChange,
		onSortModelChange,
		onFilterModelChange,
		dataManager
	} = useMuiGridServerSide({
		url: "fts/reports",
		initialPageSize: 50,
		initialSortModel: [{ name: "id", direction: "descending" }],
		otherDataParams: {
			reportType: 1,
			isActive: selectedValue?.id,
			...(selectedStartDate && selectedStartDate?.isValid() && { StartDt: dayjs(selectedStartDate).format('YYYY-MM-DD') }), // Στείλε το φίλτρο μόνο αν υπάρχει η ημερομηνία. Αν υπάρχει στείλε το με το format της βάσης
			...(selectedEndDate && selectedEndDate?.isValid() && { EndDt: dayjs(selectedEndDate).format('YYYY-MM-DD') })
		}
	});
	
	const { language, dateFormat } = useUserData()
	const handleStartDateChange = (newValue) => {
		setSelectedStartDate(newValue);
	}

	const handleEndDateChange = (newValue) => {
		setSelectedEndDate(newValue);
	}
	return (
		<Box
			sx={{
				width: '100%',
				maxWidth: '1600px',
				mx: 'auto',
				px: 2,
				mt: 2,
			}}
		>
			<Grid
				container
				//spacing={2}
				sx={{ marginLeft: 0, marginTop: '10px' }}
			>
				<Grid size={{ xs: 12, sm: 12, md: 12 }}>
					<Grid container spacing={2}>
						<Grid size={{ xs: 12, sm: 6, md: 2 }}>
							<Autocomplete
								variant="standard"
								disablePortal
								id="id-description-autocomplete"
								options={options}
								value={selectedValue}
								onChange={(event, newValue) => {
									setSelectedValue(newValue);
									dataManager({
										...(newValue && { isActive: newValue.id }),
									});
								}}
								getOptionLabel={(option) => option.Description}
								renderInput={(params) => (
									<TextField variant="standard" {...params} label={t('Projects')} />
								)}
								isOptionEqualToValue={(option, value) => option.id === value.id}
							/>
						</Grid>

						<Grid size={{ xs: 12, sm: 6, md: 2 }}>
							<DatePicker
								sx={{mt:2}}
								slotProps={{
									textField: {
										size: 'small',
										variant: 'standard'
									}
								}}
								name="StartDt"
								views={['year', 'month', 'day']}
								inputFormat={'DD-MM-YYYY'}
								format={dateFormat}
								value={selectedStartDate}
								onChange={handleStartDateChange}
							/>
						</Grid>

						<Grid size={{ xs: 12, sm: 6, md: 2 }}>
							<DatePicker
								sx={{mt:2}}
								slotProps={{
									textField: {
										size: 'small',
										variant: 'standard'
									}
								}}
								name="EndDt"
								views={['year', 'month', 'day']}
								inputFormat={'DD-MM-YYYY'}
								format={dateFormat}
								value={selectedEndDate}
								onChange={handleEndDateChange}
							/>
						</Grid>

						<Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex', alignItems: 'center' }}>
							<Button
								variant="outlined"
								color="success"
								onClick={() => {
									dataManager({
										...(selectedStartDate && selectedStartDate?.isValid() && {
											StartDt: dayjs(selectedStartDate).format('YYYY-MM-DD'),
										}),
										...(selectedEndDate && selectedEndDate?.isValid() && {
											EndDt: dayjs(selectedEndDate).format('YYYY-MM-DD'),
										}),
									});
								}}
							>
								{t('Search')}
							</Button>
							<Button
								sx={{ ml: 1 }}
								variant="outlined"
								color="error"
								onClick={() => {
									const params = {
										StartDt: dayjs(initialStartDate).format('YYYY-MM-DD'),
										EndDt: dayjs(initialEndDate).format('YYYY-MM-DD'),
									};

									const onSuccess = () => {
										setSelectedEndDate(initialEndDate);
										setSelectedStartDate(initialStartDate);
									};
									dataManager(params, onSuccess);
								}}
							>
								{t('Clear')}
							</Button>
						</Grid>
					</Grid>
				</Grid>

				<Grid size={{ xs: 12, sm: 12, md: 12 }}>
					<Box sx={{ textAlign: 'right', mt: 2 }}>
						<ExportExcelFromUrl
							url={`${API}/en/fts/reports/export`}
							data={{
								isActive: selectedValue?.id,
								reportType: 1,
								Filters: filterModel?.[0]?.predicates,
								Sort: sortModel?.[0],
								StartDt : dayjs(selectedStartDate).format('YYYY-MM-DD'),
								EndDt : dayjs(selectedEndDate).format('YYYY-MM-DD'),
							}}
						/>
					</Box>
				</Grid>

				<Grid size={{ xs: 12, md: 12 }}>
					<DataGridWrapper sx={{ height: 600 }}>
						<ProjectGridContainer
							data={data}
							isLoading={isLoading}
							page={page}
							pageSize={pageSize}
							onPaginationModelChange={onPaginationModelChange}
							total={total}
							onSortModelChange={onSortModelChange}
							onFilterModelChange={onFilterModelChange}
							language={language}
							dateFormat={dateFormat}
							isActive={selectedValue}
						/>
					</DataGridWrapper>
				</Grid>
			</Grid>
		</Box>
	);
};

export default withPermissionHOC(ProjectsReport, [access_skills])